import { useCallback, useState } from 'react';
import { Route, Routes } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import InputSearch from '@/design_system/InputSearch';
import Stack from '@/design_system/Stack';
import IconAdd from '@/icons/Add.svg';
import { useCreateEntityGroup, useEntityGroups } from '@/models/entityGroup';
import { GROUPS_PER_PAGE, GroupsTable } from '@/routes/SettingsBeta/components/GroupsTable';
import useDebouncedState from '@/utils/useDebouncedState';

import { Group as GroupPage } from './Group/Group';

export const GroupsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Groups />} />
      <Route path="/:id/*" element={<GroupPage />} />
    </Routes>
  );
};

const Groups = () => {
  const { t } = useLingui();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const resetPage = useCallback(() => setPage(1), []);
  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const { mutateAsync: createGroup, isPending: isCreateGroupPending } = useCreateEntityGroup();

  const {
    data: { entityGroups, meta } = { entityGroups: [] },
    isLoading,
    isPlaceholderData,
    isError,
  } = useEntityGroups(
    {
      limit: GROUPS_PER_PAGE,
      offset: (page - 1) * GROUPS_PER_PAGE,
      search: debouncedSearch || undefined,
    },
    { keepPreviousData: true }
  );

  return (
    <main style={{ gap: '1rem' }}>
      <Stack row gap="1rem" alignItems="center">
        <InputSearch
          ariaLabel={t({
            id: 'settings.organization.entities.groups.search.label',
            message: 'Search groups',
          })}
          placeholder={t({
            id: 'settings.organization.entities.groups.search.placeholder',
            message: 'Search groups...',
          })}
          value={search}
          onChange={debouncedSetSearch}
          style={{ flex: 1 }}
        />
        <Button
          variant="secondary"
          size="medium"
          onPress={() => {
            createGroup({
              name: t({
                id: 'settings.organization.entities.groups.add.untitled.name',
                message: 'Untitled group',
              }),
            }).then((group) => {
              navigate(`/settings_beta/network/groups/${group.id}`);
            });
          }}
          isLoading={isCreateGroupPending}
        >
          <IconAdd />
          <Trans id="settings.organization.entities.groups.add.button">Create group</Trans>
        </Button>
      </Stack>
      <GroupsTable
        entityGroups={entityGroups}
        isLoading={isLoading || isPlaceholderData}
        isError={isError}
        page={page}
        onPageChange={setPage}
        count={meta?.count}
      />
    </main>
  );
};
